import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Check } from 'lucide-react';

// Daily Movement Task - "5+ mins moving my body"
// This is a wellness anchor, not a regular task
// - Appears once per day on first app open
// - Visually distinct from user tasks
// - Disappears when completed (no Done history)
// - Resets at midnight local time

const MOVEMENT_TASK_KEY = 'wwi_daily_movement_task';

const getTodayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};

export const useDailyMovementTask = (enabled = true) => {
  const [showTask, setShowTask] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setShowTask(false);
      return;
    }

    const todayKey = getTodayKey();
    const stored = localStorage.getItem(MOVEMENT_TASK_KEY);
    
    if (stored) {
      try {
        const data = JSON.parse(stored);
        // If it's a new day, reset
        if (data.date !== todayKey) {
          setShowTask(true);
          setIsCompleted(false);
        } else {
          // Same day - check if already completed
          setShowTask(!data.completed);
          setIsCompleted(data.completed);
        }
      } catch {
        setShowTask(true);
        setIsCompleted(false);
      }
    } else {
      // First time ever
      setShowTask(true);
      setIsCompleted(false);
    }
  }, [enabled]);

  const completeTask = () => {
    const todayKey = getTodayKey();
    localStorage.setItem(MOVEMENT_TASK_KEY, JSON.stringify({
      date: todayKey,
      completed: true
    }));
    setIsCompleted(true);
    // Animate out, then hide
    setTimeout(() => {
      setShowTask(false);
    }, 300);
  };

  return { showTask, isCompleted, completeTask };
};

export const DailyMovementTask = ({ enabled = true }) => {
  const { showTask, isCompleted, completeTask } = useDailyMovementTask(enabled);

  if (!enabled || !showTask) return null;

  return (
    <AnimatePresence>
      {showTask && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: isCompleted ? 0 : 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="mb-3"
          data-testid="daily-movement-task"
        >
          <div className="bg-[#FFF8F0] border border-[#FFE8D6] rounded-xl px-3 py-2 shadow-sm scale-95 origin-top" style={{ transform: 'scaleX(1) scaleY(0.95)' }}>
            {/* Header with Daily label */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-light text-[#D4A373] uppercase tracking-wider opacity-80">
                Daily
              </span>
            </div>
            
            {/* Task content */}
            <div className="flex items-center gap-3">
              {/* Completion button */}
              <button
                onClick={completeTask}
                className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-[#F58227] hover:bg-[#F58227] hover:border-[#F58227] transition-colors group flex items-center justify-center"
                data-testid="daily-movement-complete"
              >
                <Check 
                  size={12} 
                  className="text-transparent group-hover:text-white transition-colors" 
                />
              </button>
              
              {/* Heart icon */}
              <Heart size={16} className="text-[#D4A373] flex-shrink-0" />
              
              {/* Task text */}
              <p className="text-sm text-[#52525B] font-light">
                5+ mins moving my body.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DailyMovementTask;
